import { queryOptions } from '@tanstack/react-query'
import { apiGet, apiPost } from './client'

/**
 * Settings → Easeedesk → SLA configuration.
 *
 * Perechile vechi: `settings/easeedesk/sla_settings`, `slaData`,
 * `getSubcategoriesData`, `getOperationsData`, `edit_sla_form`, `create_sla`,
 * `update_sla`, `remove_sla`.
 *
 * `apiGet` / `apiPost` pun ELE `/api/v2` in fata caii.
 */

export interface SlaRow {
  ID: number
  ci_id: number
  ci_name: string
  company_id: number
  company_name: string
  type_id: number
  type_name: string
  color_identifier: string
  category_id: number
  category_name: string
  subcategory_id: number
  subcategory_name: string
  operation_id: number
  operation_name: string
  priority_id: number
  priority_name: string
  priority_color: string
  sla: number
  DT_RowId: string
}

export interface SlaTablePage {
  draw: number
  recordsTotal: number
  recordsFiltered: number
  data: SlaRow[]
}

export interface Named {
  ID: number
  name: string
}

export interface SlaShell {
  title: string
  moduleUrl: string
  /** false cat timp coloanele ci_id / company_id nu sunt inca in baza. */
  has_service: boolean
  services: { ID: number; name: string; company_id: number; class_name: string }[]
  companies: Named[]
  types: Named[]
  priorities: Named[]
  categories: Named[]
}

export interface SlaConfiguration {
  ID: number
  ci_id: number
  company_id: number
  type_id: number
  category_id: number
  subcategory_id: number
  operation_id: number
  priority_id: number
  sla: number
}

export interface SlaConfigurationPage {
  configuration: SlaConfiguration
  subcategories: { ID: number; subcategory_name: string }[]
  operations: { ID: number; operation_name: string }[]
}

export interface SlaTableRequest {
  draw: number
  start: number
  length: number
  orderColumn: number
  orderName: string
  orderDir: 'asc' | 'desc'
  search: string
}

export function slaTableParams(r: SlaTableRequest): URLSearchParams {
  const p = new URLSearchParams()
  p.set('draw', String(r.draw))
  p.set('start', String(r.start))
  p.set('length', String(r.length))
  p.set('order[0][column]', String(r.orderColumn))
  p.set('order[0][dir]', r.orderDir)
  p.set(`columns[${r.orderColumn}][name]`, r.orderName)
  p.set('search[value]', r.search)
  return p
}

export function slaShellQuery() {
  return queryOptions({
    queryKey: ['settings', 'easeedesk', 'sla_settings'],
    queryFn: () => apiGet<SlaShell>('/settings/easeedesk/sla_settings'),
  })
}

export function slaTableQuery(r: SlaTableRequest) {
  return queryOptions({
    queryKey: ['settings', 'easeedesk', 'sla_settings', 'table', r],
    queryFn: () => apiGet<SlaTablePage>(`/settings/easeedesk/sla_settings/table?${slaTableParams(r).toString()}`),
  })
}

export function slaSubcategoriesQuery(category: number) {
  return queryOptions({
    queryKey: ['settings', 'easeedesk', 'sla_settings', 'subcategories', category],
    queryFn: () =>
      apiGet<{ subcategories: { ID: number; subcategory_name: string }[] }>(
        `/settings/easeedesk/sla_settings/subcategories/${category}`,
      ),
    enabled: category > 0,
  })
}

export function slaOperationsQuery(subcategory: number) {
  return queryOptions({
    queryKey: ['settings', 'easeedesk', 'sla_settings', 'operations', subcategory],
    queryFn: () =>
      apiGet<{ operations: { ID: number; operation_name: string }[] }>(
        `/settings/easeedesk/sla_settings/operations/${subcategory}`,
      ),
    enabled: subcategory > 0,
  })
}

export function slaConfigurationQuery(sla: number) {
  return queryOptions({
    queryKey: ['settings', 'easeedesk', 'sla_settings', 'one', sla],
    queryFn: () => apiGet<SlaConfigurationPage>(`/settings/easeedesk/sla_settings/${sla}`),
    enabled: sla > 0,
  })
}

export interface SlaFields {
  ci_id: number
  company_id: number
  type_id: number
  category_id: number
  subcategory_id: number
  operation_id: number
  priority_id: number
  sla_duration: number
}

export function createSla(fields: SlaFields) {
  return apiPost<{ ok?: boolean; ID?: number }>('/settings/easeedesk/sla_settings', { ...fields })
}

export function updateSla(sla: number, fields: SlaFields) {
  return apiPost<{ ok?: boolean }>(`/settings/easeedesk/sla_settings/${sla}`, { ...fields })
}

export function removeSla(sla: number) {
  return apiPost<{ ok?: boolean }>(`/settings/easeedesk/sla_settings/${sla}/remove`, {})
}
